import type { ReactNode } from 'react'
import { isFieldChanged } from './diff'

interface DiffFieldProps {
  label: string
  /**
   * Diff is computed by comparing these two raw values. Pass identical values
   * (or pass only `value`) for non-edit modes so the row renders neutrally.
   */
  value: unknown
  previousValue?: unknown
  /** Right-side content — the editable input or display node. */
  children: ReactNode
  /** Optional helper line beneath the input. */
  hint?: ReactNode
  /** Validation failed on this field — render the yellow ⚠️ border + tag. */
  isInvalid?: boolean
  /** Tooltip text for the invalid badge. */
  invalidReason?: string
  /** Data attribute for automated targeting and focus */
  dataField?: string
  /** Node ID attribute for automated targeting and focus */
  dataNodeId?: string
}

/**
 * Wrapper that renders a label, optional helper, and slots in the user-supplied
 * input/display node. When the value has changed vs `previousValue`, applies a
 * subtle highlight + tag so the user can see the field is part of the AI edit.
 */
export const DiffField = ({
  label,
  value,
  previousValue,
  children,
  hint,
  isInvalid,
  invalidReason,
  dataField,
  dataNodeId,
}: DiffFieldProps) => {
  const changed = previousValue !== undefined && isFieldChanged(value, previousValue)

  return (
    <label
      data-field={dataField}
      data-node-id={dataNodeId}
      className={`detailedField ${changed ? 'detailedField--changed' : ''} ${
        isInvalid ? 'detailedField--invalid' : ''
      }`}
    >
      <span className="detailedFieldLabel">
        {label}
        {changed && !isInvalid && <span className="detailedFieldTag">edited</span>}
        {isInvalid && (
          <span className="detailedFieldTag detailedFieldTag--invalid" title={invalidReason}>
            needs attention
          </span>
        )}
      </span>
      <div className="detailedFieldControl">{children}</div>
      {hint && <span className="detailedFieldHint">{hint}</span>}
      {isInvalid && invalidReason && (
        <span className="detailedFieldHint detailedFieldHint--invalid">{invalidReason}</span>
      )}
    </label>
  )
}

/**
 * Read-only display field used by the middle "baseline" column in edit mode.
 * Renders the raw value as a static block; no inputs.
 */
export const DiffFieldReadonly = ({ label, display, isMissing }: { label: string; display: ReactNode; isMissing?: boolean }) => (
  <div className="detailedField detailedField--readonly">
    <span className="detailedFieldLabel">{label}</span>
    <div className={`detailedFieldControl ${isMissing ? 'detailedFieldControl--missing' : ''}`}>
      {isMissing ? <em className="detailedFieldMissing">—</em> : display}
    </div>
  </div>
)
